import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import FadeInSection from './FadeInSection';
import VideoModal from './VideoModal';
import { caseApi } from '../services/case.api';
import { CasesApiResponse, CaseCategoryItem, CaseListItem } from '@packages/global';

type CategoryStyleEntry = { badge: string; fallbackGrad: string; icon: string; iconColor: string };

const categoryStyle: Record<string, CategoryStyleEntry> = {
  mv: { badge: 'bg-primary-600/90', fallbackGrad: 'from-primary-900/40', icon: 'music', iconColor: 'rgba(99,102,241,0.4)' },
  tvc: { badge: 'bg-accent-600/90', fallbackGrad: 'from-accent-900/40', icon: 'tv-2', iconColor: 'rgba(6,182,212,0.4)' },
  corporate: { badge: 'bg-indigo-600/90', fallbackGrad: 'from-indigo-900/40', icon: 'building-2', iconColor: 'rgba(99,102,241,0.4)' },
  drama: { badge: 'bg-purple-600/90', fallbackGrad: 'from-purple-900/40', icon: 'clapperboard', iconColor: 'rgba(167,139,250,0.4)' },
};

const CASES_PER_PAGE = 6;

const CasesSection: React.FC<{ lang: 'zh' | 'en' }> = ({ lang }) => {
  const { t } = useTranslation('website');
  const [categories, setCategories] = useState<CaseCategoryItem[]>([]);
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [selectedCase, setSelectedCase] = useState<CaseListItem | null>(null);
  const categoryIdToStyleRef = React.useRef<Record<string, CategoryStyleEntry>>({});

  useEffect(() => {
    caseApi.getCases(lang).then((data: CasesApiResponse) => {
      const styleMap: Record<string, CategoryStyleEntry> = {};
      data.categories.forEach((cat) => {
        const name = cat.label;
        if (name.toLowerCase() === 'mv' || name.toLowerCase().includes('mv')) {
          styleMap[cat.id] = categoryStyle.mv;
        } else if (name.includes('TVC') || name.includes('tvc') || name.includes('Product')) {
          styleMap[cat.id] = categoryStyle.tvc;
        } else if (name.includes('企业') || name.includes('Corporate')) {
          styleMap[cat.id] = categoryStyle.corporate;
        } else if (name.includes('短剧') || name.includes('Drama')) {
          styleMap[cat.id] = categoryStyle.drama;
        } else {
          styleMap[cat.id] = categoryStyle.mv;
        }
      });
      categoryIdToStyleRef.current = styleMap;
      setCategories(data.categories);
      setCases(data.cases);
      setLoading(false);
      setPage(1);
    }).catch(() => {
      setError(t('cases.loadError'));
      setLoading(false);
    });
  }, [lang, t]);

  const filteredCases = activeFilter === 'all' ? cases : cases.filter(c => c.categoryId === activeFilter);
  const totalPages = Math.ceil(filteredCases.length / CASES_PER_PAGE);
  const paginatedCases = filteredCases.slice((page - 1) * CASES_PER_PAGE, page * CASES_PER_PAGE);

  const handleFilterChange = (catId: string) => {
    setActiveFilter(catId);
    setPage(1);
  };

  if (loading) {
    return (
      <section id="cases" className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-dark-500">Loading...</p>
        </div>
      </section>
    );
  }

  if (!error && cases.length === 0) {
    return null;
  }

  if (error) {
    return (
      <section id="cases" className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-dark-500 text-sm col-span-3 text-center py-8">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="cases" className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-8 h-[2px] bg-accent-500" />
              <span className="text-accent-400 text-xs font-semibold tracking-wider uppercase">{t('cases.label')}</span>
              <div className="w-8 h-[2px] bg-accent-500" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4">{t('cases.title')}</h2>
          </FadeInSection>

          <FadeInSection delay="fade-in-delay-1" className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`case-filter px-5 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer ${activeFilter === cat.id ? 'active' : ''}`}
                onClick={() => handleFilterChange(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </FadeInSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedCases.map((caseItem, i) => {
              const style = categoryIdToStyleRef.current[caseItem.categoryId] || categoryStyle.mv;
              const delayClass = ['', 'fade-in-delay-1', 'fade-in-delay-2'][i % 3];
              return (
                <FadeInSection key={caseItem.id} delay={delayClass as any} className="case-card card-hover group rounded-2xl bg-dark-900/60 border border-dark-800/50 overflow-hidden cursor-pointer" onClick={() => setSelectedCase(caseItem)}>
                  <div className="relative aspect-video overflow-hidden bg-dark-900">
                    <img
                      src={caseItem.thumbnail}
                      alt={caseItem.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className={`hidden w-full h-full items-center justify-center bg-gradient-to-br ${style.fallbackGrad} to-dark-900`}>
                      <i data-lucide={style.icon} style={{ width: 40, height: 40, color: style.iconColor }} />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                        <i data-lucide="play" style={{ width: 22, height: 22, color: 'white', marginLeft: '3px' }} />
                      </div>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${style.badge} text-white backdrop-blur-sm`}>
                        {categories.find(c => c.id === caseItem.categoryId)?.label || caseItem.categoryId}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-white font-semibold mb-1 group-hover:text-primary-300 transition-colors">{caseItem.title}</h3>
                    <p className="text-dark-500 text-xs">{caseItem.desc}</p>
                  </div>
                </FadeInSection>
              );
            })}
          </div>

          {totalPages > 1 && (
            <FadeInSection className="flex items-center justify-center gap-4 mt-10">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-full border border-dark-700 text-dark-400 hover:text-white hover:border-dark-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <span className="text-sm text-dark-400">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-full border border-dark-700 text-dark-400 hover:text-white hover:border-dark-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </FadeInSection>
          )}
        </div>
      </section>

      {selectedCase && (
        <VideoModal caseItem={selectedCase} onClose={() => setSelectedCase(null)} />
      )}
    </>
  );
};

export default CasesSection;